import {
  AsyncPipe,
  DecimalPipe,
  NgClass,
  NgFor,
  NgIf,
  SlicePipe,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  Fitter,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { LocaleDatePipe } from '@sc/data-access-translation';
import {
  FitterNameDisplayContext,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { callCostLabel, ScreenUtils } from '@sc/utils';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '../../button';
import { IconComponent } from '../../icon';
import { RatingComponent } from '../../rating';
import { AppointmentCommonTimeLinesComponent } from '../appointment-common-time-lines';

@Component({
  selector: 'sc-appointment-nearest-list',
  styleUrls: ['./appointment-nearest-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    IconComponent,
    RatingComponent,
    ButtonComponent,
    AppointmentCommonTimeLinesComponent,
    FitterNamePipe,
    PhoneNumberPipe,
    MatExpansionModule,
    MatProgressSpinnerModule,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
    DecimalPipe,
    SlicePipe,
  ],
  template: `
    <mat-accordion class="group-by-fitters" [multi]="multiUnfolds">
      <ng-container
        *ngFor="
          let appointmentsFitter of getAvailableFittersWithSlot()
            | slice: 0:nbDisplayedFittersWithSlot;
          let i = index
        "
      >
        <mat-expansion-panel
          *ngIf="
            !!fittersById?.get(appointmentsFitter)?.id &&
            !displayAsCard(appointmentsFitter)
          "
          [expanded]="isExpandedAppointment(appointmentsFitter, i)"
          (afterExpand)="onAfterExpandFitter(appointmentsFitter)"
          [id]="generateFitterId(appointmentsFitter)"
          [class.has-no-appointment]="isNoApmtFitter(appointmentsFitter)"
          (mouseenter)="onFitterHover(appointmentsFitter)"
          (mouseleave)="onFitterHover('')"
        >
          <mat-expansion-panel-header
            [id]="'fitter-header-' + appointmentsFitter"
            (click)="onExpandHeaderClick(appointmentsFitter)"
            (keydown.enter)="onExpandHeaderClick(appointmentsFitter)"
            (keydown.space)="onExpandHeaderClick(appointmentsFitter)"
          >
            <mat-panel-title class="header-fitter">
              <div class="appointment-infos-header">
                <span class="dossier-progress sc-design-caption">
                  {{
                    'AppointmentNearestList.glazingSpecialist' | translate
                  }}</span
                >
                <span
                  class="mobility-no-cost"
                  *ngIf="showFitterNoCostInfo(appointmentsFitter)"
                >
                  {{ 'AppointmentNearestList.mobilityNoCost' | translate }}
                </span>
              </div>
              <div class="row">
                <h2 class="sc-design-text-2">
                  {{
                    fittersById.get(appointmentsFitter)
                      | fitterName: getFitterNameContext(appointmentsFitter)
                      | async
                  }}
                </h2>
                <sc-rating
                  *ngIf="!shouldDisplayAddress(appointmentsFitter)"
                  [rating]="fittersById.get(appointmentsFitter)?.google?.rating"
                  [brandId]="fittersById.get(appointmentsFitter)?.brandId"
                ></sc-rating>
              </div>
              <div class="row" *ngIf="shouldDisplayAddress(appointmentsFitter)">
                <div class="address-fitter sc-design-text-3">
                  <p
                    [innerHTML]="
                      fittersSB.getCompleteAddress(
                        fittersById?.get(appointmentsFitter)
                      )
                    "
                  ></p>
                </div>
                <div class="infos-fitter sc-design-text-4">
                  <div
                    class="info-fitter"
                    *ngIf="
                      fittersSB.isNotItinerant(
                        fittersById.get(appointmentsFitter)
                      )
                    "
                  >
                    <sc-icon
                      name="road"
                      specificName="appointment_nearest_list_road"
                    ></sc-icon>
                    <div class="value">
                      <p>
                        {{
                          'GlobalApplicativeConstants.distance.interventionDistanceKm'
                            | translate
                              : {
                                  distance:
                                    fittersById?.get(appointmentsFitter)
                                      ?.distance >= 0
                                      ? (fittersById?.get(appointmentsFitter)
                                          ?.distance / 1000 | number: '1.1-1')
                                      : '-'
                                }
                        }}
                      </p>
                    </div>
                  </div>

                  <span
                    class="dot sc-conf-display-google-reviews"
                    *ngIf="
                      fittersSB.showGoogleReview() &&
                      fittersSB.isNotItinerant(
                        fittersById.get(appointmentsFitter)
                      )
                    "
                    aria-hidden="true"
                  ></span>
                  <sc-rating
                    [rating]="
                      fittersById.get(appointmentsFitter)?.google?.rating
                    "
                    [brandId]="fittersById.get(appointmentsFitter)?.brandId"
                  ></sc-rating>
                </div>
              </div>
              <div
                class="row phone sc-conf-display-fitter-phone sc-design-text-3-bold"
                *ngIf="fittersById?.get(appointmentsFitter)?.phone"
              >
                <sc-icon name="phone"></sc-icon>
                <ng-container
                  *ngIf="
                    clientAppId === 'customer' &&
                      featureFlippingService.isFeatureActivated(
                        'b2c-phone-number-click-to-call-in-mobile'
                      ) &&
                      isMobileResolution();
                    else phoneText
                  "
                >
                  <a
                    [href]="
                      'tel:' +
                      (fittersById?.get(appointmentsFitter).phone
                        | phoneNumber
                        | async)
                    "
                  >
                    {{
                      fittersById?.get(appointmentsFitter).phone
                        | phoneNumber
                        | async
                    }}</a
                  ><span class="callCostLabel">{{
                    callCostLabel(fittersById?.get(appointmentsFitter).phone)
                      | translate
                  }}</span>
                </ng-container>
                <ng-template #phoneText>
                  <p>
                    {{
                      fittersById?.get(appointmentsFitter).phone
                        | phoneNumber
                        | async
                    }}<span class="callCostLabel">{{
                      callCostLabel(fittersById?.get(appointmentsFitter).phone)
                        | translate
                    }}</span>
                  </p>
                </ng-template>
              </div>
              <div
                class="row"
                *ngIf="
                  appointmentsFitter === bestScoredFittterId &&
                  highlightBestScoreFitter
                "
              >
                <div class="best-score-fitter">
                  <sc-icon name="like"></sc-icon>
                  {{ 'AppointmentNearestList.insuranceTitle' | translate }}
                </div>
              </div>
              <div class="row">
                <sc-button
                  *ngIf="
                    displaySelectFitter &&
                    shouldDisplayAddress(appointmentsFitter)
                  "
                  class="show-fitter-details-button-alt"
                  buttonType="mainWhite"
                  tabindex="0"
                  role="button"
                  (click)="onShowDetailsClick(appointmentsFitter)"
                >
                  {{ 'AppointmentNearestList.detailsButtonTitle' | translate }}
                </sc-button>
              </div>
            </mat-panel-title>
          </mat-expansion-panel-header>

          <div
            class="day"
            *ngFor="
              let day of getAllAvailableDaysInFitter(appointmentsFitter)
                | slice: 0:getNumberOfDisplayedDaysByFitter(appointmentsFitter);
              last as isLastDay
            "
            [id]="generateFitterIdDay(appointmentsFitter, day)"
          >
            <div class="day-label sc-design-text-2">
              {{
                ('GlobalApplicativeConstants.textBeforeDate' | translate) !==
                'GlobalApplicativeConstants.textBeforeDate'
                  ? ('GlobalApplicativeConstants.textBeforeDate' | translate)
                  : ''
              }}
              <h3
                class="appointment-day"
                [id]="'appointment-day-' + appointmentsFitter + '-' + day"
              >
                {{ day | localeDate: 'fullDate' | async }}
              </h3>
            </div>

            <div
              *ngIf="!isNoApmtFitter(appointmentsFitter)"
              class="slots-container"
            >
              <sc-appointment-common-time-lines
                [appointments]="
                  getAppointmentsForDayInFitter(appointmentsFitter, day)
                "
                [fitter]="fittersById?.get(appointmentsFitter)"
                [displayGarageAppointments]="displayGarageAppointments"
                [displayHomeAppointments]="displayHomeAppointments"
                [displayHomeAppointmentsAsHalfDay]="
                  displayHomeAppointmentsAsHalfDay(appointmentsFitter)
                "
                [displayOnlyReplacementVehicle]="displayOnlyReplacementVehicle"
                [displayReplacementVehicle]="displayReplacementVehicle"
                [selectedAppointment]="_selectedAppointment"
                (slotClickEvent)="onSlotClick($event)"
                [ariaLabelledby]="
                  'appointment-day-' + appointmentsFitter + '-' + day
                "
              >
                <sc-button
                  additionalAction
                  buttonType="mainWhite"
                  class="show-more-days-button"
                  [title]="
                    'AppointmentNearestList.seeMoreDaysTitle'
                      | translate
                        : {
                            fitterName:
                              fittersById.get(appointmentsFitter)
                              | fitterName
                                : getFitterNameContext(appointmentsFitter)
                              | async
                          }
                  "
                  *ngIf="
                    getNumberOfDisplayedDaysByFitter(appointmentsFitter) <
                      (appointmentsByFittersByDays?.get(appointmentsFitter)
                        ?.size || 0) && isLastDay
                  "
                  (click)="onShowMoreDaysClick(appointmentsFitter)"
                >
                  {{ 'AppointmentNearestList.seeMoreDaysLabel' | translate }}
                </sc-button>
                <sc-button
                  *ngIf="
                    displaySelectFitter &&
                    isLastDay &&
                    shouldDisplayAddress(appointmentsFitter)
                  "
                  additionalAction
                  class="show-fitter-details-button"
                  buttonType="mainWhite"
                  role="button"
                  (click)="onShowDetailsClick(appointmentsFitter)"
                >
                  {{ 'AppointmentNearestList.detailsButtonTitle' | translate }}
                </sc-button>

                <div additionalAction *ngIf="!isLastDay" class="spacer"></div>
              </sc-appointment-common-time-lines>
            </div>
          </div>
          <div
            *ngIf="isNoApmtFitter(appointmentsFitter)"
            class="no-slots-container"
          >
            <span class="no-slots-hint">{{
              'AppointmentNearestList.noDisponibility' | translate
            }}</span>
          </div>
          <div
            *ngIf="!isNoApmtFitter(appointmentsFitter)"
            class="show-more-days-container"
          >
            <button
              class="show-more-days-button"
              [title]="
                'AppointmentNearestList.seeMoreDaysTitle'
                  | translate
                    : {
                        fitterName:
                          fittersById.get(appointmentsFitter)
                          | fitterName: getFitterNameContext(appointmentsFitter)
                          | async
                      }
              "
              (click)="onShowMoreDaysClick(appointmentsFitter)"
              *ngIf="
                getNumberOfDisplayedDaysByFitter(appointmentsFitter) <
                (appointmentsByFittersByDays?.get(appointmentsFitter)?.size ||
                  0)
              "
            >
              <sc-icon
                name="more"
                [specificName]="'nearest_list_more'"
              ></sc-icon>
              <span class="sc-design-text-3-bold">
                <p>
                  {{ 'AppointmentNearestList.seeMoreDaysLabel' | translate }}
                </p>
              </span>
            </button>
          </div>
          <div
            *ngIf="displayAppointmentRequest(appointmentsFitter)"
            class="appointment-request-container"
          >
            <span class="no-good-appointment-question">{{
              'AppointmentNearestList.noGoodAppointmentQuestion' | translate
            }}</span>
            <div
              class="send-appointment-request-button"
              tabindex="0"
              role="button"
              (click)="onClickSendAppointmentRequest(appointmentsFitter)"
              (keydown.enter)="
                onClickSendAppointmentRequest(appointmentsFitter)
              "
              (keydown.space)="
                onClickSendAppointmentRequest(appointmentsFitter)
              "
            >
              <sc-icon name="folder"></sc-icon>
              <span>{{
                getRequestAppointmentBtnText(appointmentsFitter) | translate
              }}</span>
            </div>
          </div>
          <div
            class="expansion-panel-footer"
            *ngIf="
              shouldDisplayAddress(appointmentsFitter) ||
              hasReplacementRdv(appointmentsFitter)
            "
          >
            <sc-button
              *ngIf="displaySelectFitter"
              class="show-fitter-details-button"
              buttonType="mainWhite"
              role="button"
              (click)="onShowDetailsClick(appointmentsFitter)"
            >
              {{ 'AppointmentNearestList.detailsButtonTitle' | translate }}
            </sc-button>
            <div
              class="replacement-vehicle-info"
              *ngIf="hasReplacementRdv(appointmentsFitter)"
              aria-hidden="true"
            >
              <span class="replacement-vehicle-info-icon">
                <sc-icon
                  name="car"
                  specificName="replacement_vehicle"
                  [forceInitialColor]="true"
                ></sc-icon>
              </span>
              <span class="replacement-vehicle-info-text">{{
                'AppointmentNearestList.replacementVehicleAvailability'
                  | translate
              }}</span>
            </div>
          </div>
        </mat-expansion-panel>
      </ng-container>
      <ng-container
        *ngFor="
          let appointmentsFitter of getAvailableFittersNoSlot()
            | slice: 0:nbDisplayedFittersNoSlot;
          let i = index
        "
      >
        <div
          *ngIf="
            !!fittersById?.get(appointmentsFitter)?.id &&
            displayAsCard(appointmentsFitter)
          "
          class="appointment-card"
          [class.request-only-to-customer]="
            isFitterRequestOnlyToCustomer(appointmentsFitter)
          "
          (mouseenter)="onFitterHover(appointmentsFitter)"
          (mouseleave)="onFitterHover('')"
        >
          <mat-panel-title class="header-fitter">
            <div class="appointment-infos-header">
              <span
                class="dossier-progress sc-design-caption"
                [ngClass]="{
                  generalFitter: this.isFitterGeneralist(
                    fittersById.get(appointmentsFitter).brandId
                  ),
                  glazingSpecialist: !this.isFitterGeneralist(
                    fittersById.get(appointmentsFitter).brandId
                  )
                }"
                >{{
                  (this.isFitterGeneralist(
                    fittersById.get(appointmentsFitter).brandId
                  )
                    ? 'AppointmentNearestList.generalFitter'
                    : 'AppointmentNearestList.glazingSpecialist'
                  ) | translate
                }}</span
              >
            </div>
            <div class="row">
              <h2 class="sc-design-text-2">
                {{
                  fittersById.get(appointmentsFitter)
                    | fitterName: getFitterNameContext(appointmentsFitter)
                    | async
                }}
              </h2>
              <sc-rating
                *ngIf="!shouldDisplayAddress(appointmentsFitter)"
                [rating]="fittersById.get(appointmentsFitter)?.google?.rating"
                [brandId]="fittersById.get(appointmentsFitter)?.brandId"
              ></sc-rating>
            </div>
            <div class="row sub-name-card-info">
              <p class="sc-design-text-4-bold">
                {{ 'AppointmentNearestList.subNameCardInfo' | translate }}
              </p>
            </div>
            <div class="row" *ngIf="shouldDisplayAddress(appointmentsFitter)">
              <div class="address-fitter sc-design-text-3">
                <p
                  [innerHTML]="
                    fittersSB.getCompleteAddress(
                      fittersById.get(appointmentsFitter)
                    )
                  "
                ></p>
              </div>
              <div class="infos-fitter sc-design-text-4">
                <div
                  class="info-fitter"
                  *ngIf="
                    fittersSB.isNotItinerant(
                      fittersById.get(appointmentsFitter)
                    )
                  "
                >
                  <sc-icon
                    name="road"
                    specificName="appointment_nearest_list_road"
                  ></sc-icon>
                  <div class="value">
                    <p>
                      {{
                        'GlobalApplicativeConstants.distance.interventionDistanceKm'
                          | translate
                            : {
                                distance:
                                  fittersById.get(appointmentsFitter)
                                    ?.distance >= 0
                                    ? (fittersById.get(appointmentsFitter)
                                        ?.distance / 1000 | number: '1.1-1')
                                    : '-'
                              }
                      }}
                    </p>
                  </div>
                </div>

                <span
                  class="dot sc-conf-display-google-reviews"
                  *ngIf="
                    fittersSB.showGoogleReview() &&
                    fittersSB.isNotItinerant(
                      fittersById.get(appointmentsFitter)
                    )
                  "
                  aria-hidden="true"
                ></span>
                <sc-rating
                  [rating]="fittersById.get(appointmentsFitter).google?.rating"
                  [brandId]="fittersById.get(appointmentsFitter)?.brandId"
                ></sc-rating>
              </div>
            </div>
            <div
              class="row phone sc-conf-display-fitter-phone sc-design-text-3-bold"
              *ngIf="fittersById.get(appointmentsFitter).phone"
            >
              <sc-icon name="phone"></sc-icon>
              <p>
                {{ fittersById.get(appointmentsFitter).phone }}
                <span class="callCostLabel">{{
                  callCostLabel(fittersById.get(appointmentsFitter).phone)
                    | translate
                }}</span>
              </p>
            </div>
            <div class="row">
              <sc-button
                *ngIf="
                  displaySelectFitter &&
                  shouldDisplayAddress(appointmentsFitter)
                "
                class="show-fitter-details-button-alt"
                buttonType="mainWhite"
                tabindex="0"
                role="button"
                (click)="onShowDetailsClick(appointmentsFitter)"
              >
                {{ 'AppointmentNearestList.detailsButtonTitle' | translate }}
              </sc-button>
            </div>
          </mat-panel-title>
          <div
            *ngIf="isNoApmtFitter(appointmentsFitter)"
            class="no-slots-container"
          >
            <span class="no-slots-hint">{{
              getNoSlotDescription(appointmentsFitter) | translate
            }}</span>
          </div>
          <div class="expansion-panel-footer">
            <sc-button
              *ngIf="
                displaySelectFitter && shouldDisplayAddress(appointmentsFitter)
              "
              class="show-fitter-details-button"
              buttonType="mainWhite"
              role="button"
              (click)="onShowDetailsClick(appointmentsFitter)"
            >
              {{ 'AppointmentNearestList.detailsButtonTitle' | translate }}
            </sc-button>
            <sc-button
              *ngIf="displayAppointmentRequest(appointmentsFitter)"
              class="send-appointment-request-button"
              role="button"
              (click)="onClickSendAppointmentRequest(appointmentsFitter)"
            >
              {{ getRequestAppointmentBtnText(appointmentsFitter) | translate }}
            </sc-button>
          </div>
        </div>
      </ng-container>
      <button
        *ngIf="hasMoreFittersToShow || loading"
        class="show-more-fitters-button"
        [title]="
          'AppointmentNearestList.seeMoreFittersTitle'
            | translate: { clientAppId: this.clientAppId }
        "
        (click)="onShowMoreFittersClick()"
      >
        <mat-spinner
          *ngIf="loading"
          class="mat-spinner-color"
          [diameter]="20"
        ></mat-spinner>
        <sc-icon
          *ngIf="!loading"
          name="more"
          specificName="nearest_list_more"
        ></sc-icon>
        <span class="sc-design-text-3-bold">
          <p>
            {{
              'AppointmentNearestList.seeMoreFittersLabel'
                | translate: { clientAppId: this.clientAppId }
            }}
          </p>
        </span>
      </button>
    </mat-accordion>
  `,
})
export class AppointmentNearestListComponent implements OnChanges {
  @Input() displayHomeAppointments = true;
  @Input() displayGarageAppointments = true;
  @Input() displayReplacementVehicle = true;
  @Input() displayOnlyReplacementVehicle = false;
  @Input() hasMoreFittersToShow = true;
  @Input() numbDisplayedDays = 3;
  @Input() multiUnfolds = true;
  @Input() unfoldsAll = false;
  @Input() selectedFitterId: string;
  @Input() dossier: Dossier;
  _selectedAppointment: Appointment;
  @Input() set selectedAppointment(selectedAppointment: Appointment) {
    this._selectedAppointment = selectedAppointment;
    if (!!selectedAppointment)
      this.selectedFitterId = selectedAppointment.fitterId;
  }
  @Input() displaySelectFitter = true;
  @Input() highlightBestScoreFitter = false;
  @Input() set selectedDay(day) {
    if (day && this.appointmentsByFittersByDays.size) {
      this.scrollToSelectedDate(day);
    }
  }
  @Input() set noDotfittersIDWithOutsideApp(
    noDotfittersIDWithOutsideApp: string[]
  ) {
    this._currentNoDotFittersIDWithOutsideApp = noDotfittersIDWithOutsideApp;
  }
  @Input() weatherEventFitters: Fitter[];

  @Input() appointmentsByFittersByDays: Map<string, Map<string, Appointment[]>>;
  @Input() fittersById: Map<string, Fitter>;
  @Input() bestScoredFittterId: string;
  // NOTE - Difference between noApppointment and noSlot : noSlot === search version NO_SLOT / noAppointment === !noSlot && !appointment.length && canDoRequests
  @Input() noAppointmentFitters: Fitter[];
  @Input() noSlotFitters: Fitter[];
  @Input() loading: boolean;
  @Input() nbDisplayedFittersWithSlot: number;
  @Input() nbDisplayedFittersNoSlot: number;

  @Output() slotClickEvent = new EventEmitter<Appointment>();
  @Output() requestAppointment = new EventEmitter<string>();
  @Output() showFitterClickEvent = new EventEmitter<{ brandId; fitterId }>();
  @Output() fitterHoverEvent = new EventEmitter<string>();
  @Output() showMoreFittersEvent = new EventEmitter<void>();

  numberDisplayOfDaysByFitters: {
    fitterId: string;
    numberOfDisplayDays: number;
  }[] = [];

  _currentNoDotFittersIDWithOutsideApp: string[] = [];

  callCostLabel = callCostLabel;

  constructor(
    private readonly sandboxService: SandboxService,
    public fittersSB: FittersSandboxService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly configurationService: ConfigurationService,
    private readonly dossierSB: DossierSandboxService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const appMapChanges = changes.appointmentsByFittersByDays;

    if (
      appMapChanges &&
      appMapChanges.currentValue !== appMapChanges.previousValue
    ) {
      // Rebuild numberDisplayOfDaysByFitters list
      appMapChanges?.currentValue?.forEach((value, key) => {
        if (
          this.numberDisplayOfDaysByFitters.findIndex(
            (item) => item.fitterId === key
          ) === -1
        ) {
          this.numberDisplayOfDaysByFitters.push({
            fitterId: key,
            numberOfDisplayDays: this.numbDisplayedDays,
          });
        }
      });

      const orderedFitterIds = Array.from(
        this.appointmentsByFittersByDays.keys()
      );
      this.numberDisplayOfDaysByFitters.sort((item1, item2) => {
        return (
          orderedFitterIds.indexOf(item1.fitterId) -
          orderedFitterIds.indexOf(item2.fitterId)
        );
      });
    }
  }

  isMobileResolution(): boolean {
    return ScreenUtils.isMobileResolution();
  }

  isExpandedAppointment(appointmentsFitter: string, i: number) {
    const { unfoldsAll } = this;
    if (unfoldsAll || this.selectedFitterId === appointmentsFitter) {
      return true;
    }
    const firstOpen = this.getAllAvailableFitters().findIndex((fitterId) =>
      this.featureFlippingService?.isFeatureActivated('expand-mobility-first')
        ? !this.shouldDisplayAddress(fitterId)
        : this.shouldDisplayAddress(fitterId)
    );
    return (
      !this.selectedFitterId && (firstOpen === -1 ? i === 0 : i === firstOpen)
    );
  }

  generateFitterId(appointmentsFitter: string) {
    return 'fitter_' + appointmentsFitter;
  }

  generateFitterIdDay(appointmentsFitter: string, day: string) {
    return 'fitter' + appointmentsFitter + 'day' + day;
  }

  getAllAvailableFitters() {
    return Array.from(this.appointmentsByFittersByDays?.keys() || []).concat(
      this.noAppointmentFitters.map((fitter) => fitter.id)
    );
  }

  getAvailableFittersWithSlot() {
    return Array.from(this.appointmentsByFittersByDays?.keys() || []).concat(
      this.noAppointmentFitters
        .filter((fitter) => !this.isNoSlotFitter(fitter.id))
        .map((fitter) => fitter.id)
    );
  }

  getAvailableFittersNoSlot() {
    return this.noSlotFitters.map((fitter) => fitter.id);
  }

  getAllAvailableDaysInFitter(fitterId: string) {
    return Array.from(
      this.appointmentsByFittersByDays?.get(fitterId)?.keys() || []
    );
  }

  getAppointmentsForDayInFitter(fitterId: string, day: string) {
    return Array.from(
      this.appointmentsByFittersByDays?.get(fitterId)?.get(day)?.values() || []
    );
  }

  displayHomeAppointmentsAsHalfDay(fitterId: string) {
    return this.featureFlippingService.isBrandFeatureActivated(
      this.fittersById.get(fitterId).brandId,
      'display-PAD-as-half-day'
    );
  }

  isNoApmtFitter(fitterId: string) {
    return !!this.noAppointmentFitters.some((f) => f.id === fitterId);
  }

  isNoSlotFitter(fitterId: string) {
    const brandId = this.fittersById.get(fitterId)?.brandId;
    return brandId
      ? this.configurationService.getBrandSearchVersion(brandId) === 'NO_SLOT'
      : false;
  }

  isWeatherEventFitter(fitterId: string) {
    return this.weatherEventFitters.some((f) => f.id === fitterId);
  }

  displayAsCard(fitterId: string) {
    return this.isNoSlotFitter(fitterId) || this.isWeatherEventFitter(fitterId);
  }

  isFitterRequestOnlyToCustomer(fitterId: string) {
    const brandId = this.fittersById.get(fitterId)?.brandId;
    return this.featureFlippingService.sendRequestNotificationOnlyToCustomer(
      brandId
    );
  }

  displayAppointmentRequest(fitterId: string) {
    const fitterBrandId = this.fittersById.get(fitterId)?.brandId;

    const isAlreadyRequested =
      !this.dossier?.appointment &&
      (this.dossier?.requestAppointment?.status === 'REQUESTED' ||
        this.dossier?.requestAppointment?.status === 'MODIFIED') &&
      this.dossier?.requestAppointment?.fitterId === fitterId;

    return (
      this.sandboxService.canDoRequestAppointment(fitterBrandId) &&
      !isAlreadyRequested
    );
  }

  getOnlyCustomerKey(appointmentsFitter: string) {
    return this.isFitterRequestOnlyToCustomer(appointmentsFitter)
      ? 'onlyCustomer'
      : 'all';
  }

  getNoSlotDescription(appointmentsFitter: string) {
    return `AppointmentNearestList.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey(appointmentsFitter)}.noSlotDescription`;
  }

  getRequestAppointmentBtnText(appointmentsFitter: string) {
    return `AppointmentNearestList.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey(appointmentsFitter)}.apmtRequestBtn`;
  }

  onAfterExpandFitter(appointmentsFitter: string) {
    setTimeout(() => {
      const element = document.getElementById(
        this.generateFitterId(appointmentsFitter)
      );
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      this.changeDetector.markForCheck();
    }, 100);
  }

  scrollToSelectedDate(day) {
    const fitterId = !this.selectedFitterId
      ? this.numberDisplayOfDaysByFitters[0]?.fitterId
      : this.selectedFitterId;

    this.selectedFitterId = fitterId;

    const date = new Date(day);
    const id = this.generateFitterIdDay(fitterId, date.toISOString());
    this.onShowMoreDaysClick(fitterId, date);
    setTimeout(() => {
      const elementToFocus = document.getElementById(
        this.generateFitterId(fitterId)
      );
      if (elementToFocus) {
        if (
          (elementToFocus?.firstElementChild as HTMLElement)?.ariaExpanded ==
          'false'
        ) {
          (elementToFocus?.firstElementChild as HTMLElement)?.click();
        }

        (elementToFocus?.firstElementChild as HTMLElement)?.focus();

        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }
    }, 1000);
  }

  onExpandHeaderClick(fitterId: string) {
    this.selectedFitterId = fitterId;
  }

  onShowDetailsClick(fitterId: string) {
    const brandId = this.fittersById?.get(fitterId)?.brandId;
    this.showFitterClickEvent.emit({ brandId: brandId, fitterId: fitterId });
  }

  getNumberOfDisplayedDaysByFitter(appointmentsFitter: string) {
    return this.numberDisplayOfDaysByFitters.find(
      (e) => e.fitterId === appointmentsFitter
    )?.numberOfDisplayDays;
  }

  hasReplacementRdv(appointmentsFitter: string) {
    const brandId = this.fittersById?.get(appointmentsFitter)?.brandId;
    const fitterHasReplacementVehicleFeature =
      this.displayReplacementVehicle &&
      this.featureFlippingService?.isBrandFeatureActivated(
        brandId,
        'can-declare-replacement-vehicle-fitter'
      );
    if (!fitterHasReplacementVehicleFeature) {
      return false;
    } else {
      for (const appointmentList of this.appointmentsByFittersByDays
        ?.get(appointmentsFitter)
        ?.values() || []) {
        for (const appointment of appointmentList) {
          if (appointment?.replacementVehicle) return true;
        }
      }
      return false;
    }
  }

  onShowMoreDaysClick(appointmentsFitter: string, dayToDisplay?) {
    if (appointmentsFitter) {
      const numbDays =
        this.appointmentsByFittersByDays.get(appointmentsFitter)?.size || 0;
      const numberOfDisplayDays =
        this.getNumberOfDisplayedDaysByFitter(appointmentsFitter);
      const numbHiddenDays = numbDays - numberOfDisplayDays;
      if (dayToDisplay) {
        const index = Array.from(
          this.appointmentsByFittersByDays.get(appointmentsFitter)?.keys() || []
        ).indexOf(dayToDisplay.toISOString());
        if (this.numberDisplayOfDaysByFitters[0]?.numberOfDisplayDays < index) {
          this.numberDisplayOfDaysByFitters =
            this.numberDisplayOfDaysByFitters.map((entries) => {
              if (entries.fitterId === appointmentsFitter) {
                return {
                  ...entries,
                  numberOfDisplayDays: index + 1,
                };
              } else return entries;
            });
          this.changeDetector.markForCheck();
        }
      } else {
        this.numberDisplayOfDaysByFitters =
          this.numberDisplayOfDaysByFitters.map((entries) => {
            if (entries.fitterId === appointmentsFitter) {
              return {
                ...entries,
                numberOfDisplayDays:
                  numbHiddenDays - 2 > 0 ? numberOfDisplayDays + 2 : numbDays,
              };
            } else return entries;
          });
      }
    }
  }

  onShowMoreFittersClick() {
    this.showMoreFittersEvent.emit();
  }

  onSlotClick($event) {
    this.slotClickEvent.emit($event);
  }

  onFitterHover(event) {
    if (this.displayGarageAppointments) {
      this.fitterHoverEvent.emit(event);
    }
  }

  onClickSendAppointmentRequest(fitterId: string) {
    this.requestAppointment.emit(fitterId);
  }

  shouldDisplayAddress(fitterId: string) {
    return (
      this.fittersSB.isNotItinerant(this.fittersById.get(fitterId)) &&
      this._currentNoDotFittersIDWithOutsideApp.indexOf(fitterId) == -1
    );
  }

  getFitterNameContext(fitterId: string) {
    if (!this.shouldDisplayAddress(fitterId)) {
      return FitterNameDisplayContext.SlotListOnlyMobility;
    } else {
      return undefined;
    }
  }

  isFitterGeneralist(brandID: string) {
    return this.featureFlippingService.isBrandFeatureActivated(
      brandID,
      'fitter-generalist-service'
    );
  }

  isFitterAlreadyRequested(fitterId: string) {
    return this.dossierSB.dossier$.pipe(
      map(
        (dossier) =>
          !dossier?.appointment &&
          dossier?.requestAppointment?.status === 'REQUESTED' &&
          dossier?.requestAppointment?.fitterId === fitterId
      )
    );
  }

  showFitterNoCostInfo(fitterId: string) {
    const brandId = this.fittersById.get(fitterId)?.brandId;
    return (
      !this.shouldDisplayAddress(fitterId) &&
      this.featureFlippingService.isBrandFeatureActivated(
        brandId,
        'show-mobility-no-cost-info'
      )
    );
  }
}
